import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Lock, Unlock, ArrowLeft, Ban, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCardStatusBadge } from '../utils/cardStatus';
import OTPVerificationModal from './OTPVerificationModal';
import { SESSION_KEY } from '../helper/common';
const config = {
  apiUrl: process.env.REACT_APP_API_URL_User,
  labels: {
    selectCard: 'Select a Card',
  },
};

const CardBlockManagement = () => {
  const navigate = useNavigate();
  const [cardList, setCardList] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentCardStatus, setCurrentCardStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'block' | 'unblock'

  useEffect(() => {
    fetchCardList();
  }, []);

  const fetchCardList = async () => {
    try {
      setLoading(true);
      setError(null);
      const custId = localStorage.getItem('custId');
      if (!custId) {
        throw new Error('Customer ID not found. Please visit the dashboard.');
      }
      const token = localStorage.getItem(SESSION_KEY);
      const response = await axios.get(`${config.apiUrl}/api/user/list`, {
        params: { custId },
        headers: { Authorization: `Bearer ${token}` },
      });

      const cardsData = response.data.cards || [];
      setCardList(cardsData);

      const storedCardNumber = localStorage.getItem('cardNumber');
      const storedCardStatus = localStorage.getItem('cardStatus') || 'A';
      if (storedCardNumber && cardsData.length > 0) {
        const card = cardsData.find((c) => c.cardNumber === storedCardNumber);
        if (card) {
          setSelectedCard(card);
          setCurrentCardStatus(card.status || storedCardStatus);
        }
      }

      if (cardsData.length > 0 && !selectedCard) {
        setSelectedCard(cardsData[0]);
        setCurrentCardStatus(cardsData[0].status || 'A');
      }
    } catch (err) {
      console.error('Error fetching card list:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load card list';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCardSelect = (e) => {
    const cardId = parseInt(e.target.value);
    const card = cardList.find((c) => c.id === cardId);
    if (card) {
      setSelectedCard(card);
      setCurrentCardStatus(card.status);
      localStorage.setItem('cardNumber', card.cardNumber);
      localStorage.setItem('cardStatus', card.status);
      setAgreeToTerms(false);
    }
  };

  const executeBlockCard = async () => {
    setIsProcessing(true);
    const token = localStorage.getItem(SESSION_KEY);
    try {
      const response = await axios.post(
        `${config.apiUrl}/api/cards/temporary-block`,
        { cardNumber: selectedCard.cardNumber },
        {
          headers: {
            'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        const keys = Object.keys(result);
        const message = result.message || (keys.length > 0 ? result[keys[0]] : null) || 'Card has been blocked successfully!';
        toast.success(message);
        setCurrentCardStatus('T');
        setAgreeToTerms(false);
        localStorage.setItem('cardStatus', 'T');
        const updatedCardList = cardList.map((card) =>
          card.cardNumber === selectedCard.cardNumber ? { ...card, status: 'T' } : card
        );
        setCardList(updatedCardList);
        setSelectedCard({ ...selectedCard, status: 'T' });
      } else {
        toast.error('Failed to block card');
      }
    } catch (error) {
      console.error('Network error:', error);
      toast.error('Network error while blocking card');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBlockCard = () => {
    if (!selectedCard) { toast.error('Please select a card to block'); return; }
    if (selectedCard.status !== 'A') { toast.error('Card is already blocked'); return; }
    if (!agreeToTerms) { toast.error('Please agree to the terms and conditions'); return; }
    setPendingAction('block');
    setShowOTPModal(true);
  };

  const executeUnblockCard = async () => {
    setIsProcessing(true);

    try {
      const token = localStorage.getItem(SESSION_KEY);
      const response = await axios.post(
        `${config.apiUrl}/api/cards/unblock`,
        { cardNumber: selectedCard.cardNumber },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        if (result.success !== undefined) {
          if (result.success) {
            toast.success(result.message || 'Card has been unblocked successfully!');
            setCurrentCardStatus('A');
            setAgreeToTerms(false);
            localStorage.setItem('cardStatus', 'A');
            const updatedCardList = cardList.map((card) =>
              card.cardNumber === selectedCard.cardNumber ? { ...card, status: 'A' } : card
            );
            setCardList(updatedCardList);
            setSelectedCard({ ...selectedCard, status: 'A' });
          } else {
            toast.error(result.error || result.message || 'Failed to unblock card');
          }
        } else {
          const keys = Object.keys(result);
          if (keys.length > 0) {
            const isSuccess = keys[0] === 'true';
            const message = result[keys[0]];
            if (isSuccess) {
              toast.success(message);
              setCurrentCardStatus('A');
              setAgreeToTerms(false);
              localStorage.setItem('cardStatus', 'A');
              const updatedCardList = cardList.map((card) =>
                card.cardNumber === selectedCard.cardNumber ? { ...card, status: 'A' } : card
              );
              setCardList(updatedCardList);
              setSelectedCard({ ...selectedCard, status: 'A' });
            } else {
              toast.error(message);
            }
          }
        }
      } else {
        toast.error('Failed to unblock card');
      }
    } catch (error) {
      console.error('Network error:', error);
      toast.error('Only temporary block card can be activated. Please process replacement.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnblockCard = async () => {
    if (!selectedCard) { toast.error('Please select a card to unblock'); return; }
    if (selectedCard.status === 'A') { toast.error('Card is already active'); return; }
    if (['S', 'L', 'B'].includes(selectedCard.status)) {
      toast.error('Only temporary block card can be activated. Please process replacement.');
      return;
    }

    else if (selectedCard.status === 'U') {
      toast.error('Only temporary block card can be activated. To activate your card, please use MCB ATM or call the helpline at 11000662.');
      return;
    }

    if (!agreeToTerms) { toast.error('Please agree to the terms and conditions'); return; }

    if (selectedCard.status === 'T') {
      setPendingAction('unblock');
      setShowOTPModal(true);
    } else {
      setIsProcessing(true);
      try {
        const token = localStorage.getItem(SESSION_KEY);
        const response = await axios.post(
          `${config.apiUrl}/api/cards/unblock`,
          { cardNumber: selectedCard.cardNumber },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            }
          }
        );
        const result = response.data;
        const keys = Object.keys(result);
        const message = result.message || (keys.length > 0 ? result[keys[0]] : null) || 'Card has been unblocked successfully!';
        toast.success(message);
      } catch (error) {
        const errorMsg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Only temporary block card can be activated.';
        toast.error(errorMsg);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading cards...</div>
      </div>
    );
  }

  if (error || cardList.length === 0) {
    return (
      <section className="min-h-screen py-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-xl p-8 max-w-md mx-auto">
              <CreditCard className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-800 dark:text-red-200 mb-2">No Card Found</h3>
              <p className="text-red-700 dark:text-red-300 mb-4">
                {error || 'Please visit the dashboard first to load your card information.'}
              </p>
              <button
                onClick={() => navigate('/main')}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const isBlocked = selectedCard && selectedCard.status !== 'A';

  return (
    <>
      <OTPVerificationModal
        isOpen={showOTPModal}
        actionLabel={pendingAction === 'block' ? 'Block Card' : 'Unblock Card'}
        onSuccess={() => {
          setShowOTPModal(false);
          if (pendingAction === 'block') executeBlockCard();
          else if (pendingAction === 'unblock') executeUnblockCard();
          setPendingAction(null);
        }}
        onCancel={() => { setShowOTPModal(false); setPendingAction(null); }}
      />
      <section className="min-h-screen py-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <button
              onClick={() => navigate('/CardManage')}
              className="flex items-center space-x-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-6 mx-auto"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Card Settings</span>
            </button>

            <div className="flex justify-center mb-4">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/50 dark:to-blue-800/50 rounded-full">
                <CreditCard className="h-12 w-12 text-blue-600 dark:text-blue-400" />
              </div>
            </div>

            <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4 bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-400 dark:to-blue-500 bg-clip-text text-transparent">
              Block / Unblock Card
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-lg mb-4">
              Manage your card blocking status
            </p>

            {cardList.length > 0 && (
              <div className="max-w-md mx-auto mb-6">
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                  {config.labels.selectCard}
                </label>
                <select
                  value={selectedCard ? selectedCard.id : ''}
                  onChange={handleCardSelect}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 focus:border-transparent transition-all"
                >
                  <option value="" disabled>
                    Select a card
                  </option>
                  {cardList.map((card) => (
                    <option key={card.id} value={card.id}>
                      {card.cardholderName} (•••• {card.cardNumber.slice(-4)})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </motion.div>

          {selectedCard && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-slate-200/50 dark:bg-gray-800/80 dark:border-gray-700/50"
            >
              {/* <div className="flex justify-center items-center space-x-2 mb-6">
              <p className="text-slate-500 dark:text-slate-500 text-sm">
                Customer Card: •••• {selectedCard.cardNumber.slice(-4)}
              </p>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center bg-${statusBadge.color}-100 dark:bg-${statusBadge.color}-900/50 text-${statusBadge.color}-800 dark:text-${statusBadge.color}-300`}
              >
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusBadge.label}
              </span>
            </div> */}


              <div className="flex justify-center items-center space-x-2 mb-6">
                <p className="text-slate-500 dark:text-slate-500 text-sm">
                  Customer Card: •••• {selectedCard.cardNumber.slice(-4)}
                </p>

                {(() => {
                  const statusBadge = getCardStatusBadge(selectedCard.status);
                  const StatusIcon = statusBadge.icon;

                  return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center ${statusBadge.classes}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusBadge.label}
                    </span>
                  );
                })()}
              </div>


              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <p className="font-semibold mb-1">Important Information:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {[
                        'Blocking your card will prevent all transactions',
                        'You can unblock your card at any time',
                        'Blocked cards cannot be used for purchases or withdrawals',
                      ].map((item, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: 0.4 + i * 0.1 }}
                        >
                          {item}
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-600"
                  />
                  <span className="text-slate-600 dark:text-slate-400">
                    I understand and agree to {isBlocked ? 'unblock' : 'block'} this card
                  </span>
                </label>
              </div>

              <div className="flex gap-4">
                {isBlocked ? (
                  <button
                    onClick={handleUnblockCard}
                    disabled={!agreeToTerms || isProcessing}
                    className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 dark:from-green-500 dark:to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Unblocking...</span>
                      </>
                    ) : (
                      <>
                        <Unlock className="h-5 w-5" />
                        <span>Unblock Card</span>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleBlockCard}
                    disabled={!agreeToTerms || isProcessing}
                    className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-red-600 to-red-700 dark:from-red-500 dark:to-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Blocking...</span>
                      </>
                    ) : (
                      <>
                        <Lock className="h-5 w-5" />
                        <span>Block Card</span>
                      </>
                    )}
                  </button>
                )}

                <button
                  onClick={() => navigate('/CardManage')}
                  className="flex-1 flex items-center justify-center space-x-2 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  <CreditCard className="h-5 w-5" />
                  <span>Card Settings</span>
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
};

export default CardBlockManagement;
